import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { defaultKey } from '../../utils/config-keys';
import {
  readConfigKeys,
  readConfigs,
  saveConfigs,
  activateConfig,
  readActiveConfig,
} from '../../actions/configs';
import { readFilters} from '../../actions/filters';
import { readImages } from '../../actions/images';
import { getNextNameWithSequenceNumber } from '../../libs/text';
import ConfigKeyList from '../../components/config-key-list';
import ConfigurationsEditor from '../configurations-editor';
import Spinner from '../../components/spinner';
import style from './style.css';
import classNames from 'classnames';
import { validateKey, ErrorCode as KeyErrorCode } from '../../../../common/validation/key';
import { checkConfigAndPromptIfError } from '../../utils/validate-config';

class Dashboard extends React.Component {

  // TODO: Handle state where user browses to non existing config url
  constructor(props) {
    super(props);

    this.onCopyConfigsClick = this.onCopyConfigsClick.bind(this);
    this.onCreateConfigsClick = this.onCreateConfigsClick.bind(this);
    this.onActiveKeyChange = this.onActiveKeyChange.bind(this);
  }

  componentDidMount() {
    const { dispatch, match, history, configs } = this.props;
    const { key } = match.params;

    dispatch(readConfigKeys());
    dispatch(readImages());
    dispatch(readFilters());
    dispatch(readActiveConfig())
      .then(action => {
        // Only read active config if not already selected (in which case it will be
        // read anyhow), so we can display the active send date in the ui without
        // first having to select it.
        const { payload } = action;
        if (payload.key !== key) {
          dispatch(readConfigs(payload.key));
        }
      });

    if (key && !configs.byKey[key]) {
      dispatch(readConfigs(key));
    }

    this.unlistenHistory = history.listen((location) => {
      const { pathname } = location;
      const { configs } = this.props;

      // `match` found is `this.props` is not up to date at this point, so
      // construct a new match object based on current location.
      const match = matchPath(pathname, {
        path: '/redigera/:key?',
      });

      const { key } = match.params;

      if (key && !configs.byKey[key]) {
        dispatch(readConfigs(key));
      }
    });
  }

  componentWillUnmount() {
    this.unlistenHistory();
  }

  render() {
    const { match, configs, simulations } = this.props;
    const { key } = match.params;
    const config = configs.byKey[key];

    return (
      <main>
        <section className={ style.grid }>
          <div className={ style.fullRow }>
            <h2 className={ style.mainTitle }>Redigera annonser</h2>
          </div>
        </section>
        <section className={ style.grid }>
          <div className={ style.fullRow }>
            <h2 className={ style.subTitle }>Utskick</h2>
            <div
              className={ classNames(style.availableKeys, {
                [style.center]: configs.isFetchingKeys,
              }) }
            >
              {
                configs.isFetchingKeys
                  ? <Spinner />
                  : <ConfigKeyList
                    keys={ configs.allKeys.map(key => {
                      const config = configs.byKey[key];
                      return {
                        value: key,
                        isSaved: config ? config.isSaved : true,
                      };
                    })}
                    currentKey={ key }
                    activeKey={ configs.activeKey }
                    activeDate={
                      configs.activeKey && configs.byKey[configs.activeKey]
                        ? configs.byKey[configs.activeKey].sendDate
                        : null
                    }
                    onKeyChange={ this.onActiveKeyChange }
                    isChangingKey={ configs.isFetchingActiveKey }
                  />
              }
            </div>
          </div>
        </section>
        <setion className={ style.grid }>
          <div className={ style.halfRow }>
            <button
              className={ style.createConfigButton }
              disabled={ configs.isFetchingKeys || configs.fetchKeysError }
              onClick={ this.onCreateConfigsClick }>Skapa nytt utskick</button>
          </div>
          <div className={ style.halfRow}>
            {
              key
                ? (
                  <button
                    className={ style.copyConfigButton }
                    disabled={ !config || (!config && configs.isFetchingConfigs) }
                    onClick={ this.onCopyConfigsClick }
                  >{`Kopiera utskick ${ key }`}</button>
                )
                : null
            }
          </div>
        </setion>
        <section className={ style.grid }>
          <div className={ style.fullRow }>
            {
              key
                ? (
                  <div className={ style.halfRow }>
                    <h2 className={ style.subTitle }>{`Annonser i ${ key }`}</h2>
                  </div>
                )
                : null
            }
            {
              key
                ? (
                  <ConfigurationsEditor currentKey={ key } />
                )
                : null
            }
          </div>
        </section>
        {
          simulations.isRunning
            ? (
              <div className={ style.overlay }>
                <div className={ style.overlayContent}>
                  <h1 className={ style.overlayTitle }>Simulering körs</h1>
                  <div className={ style.center }>
                    <Spinner/>
                  </div>
                </div>
              </div>
            ) : (
              null
            )
        }
      </main>
    );
  }

  onCreateConfigsClick(/*event*/) {
    const { dispatch, history } = this.props;

    const key = this.promptForName();
    if (!key) {
      return;
    }

    // Create configs with two empty configs and empty save date, then navigate
    // to new config
    dispatch(saveConfigs(key, [{slot: 0}, {slot: 1}], ''))
      .then(() => history.push(`/redigera/${key}`));
  }

  onCopyConfigsClick(/*event*/) {
    const { match, configs, dispatch, history } = this.props;
    const { key } = match.params;

    const keyCopy = this.promptForName();
    if (!keyCopy) {
      return;
    }

    // Copy specified configs and reset send date, then navigate to new config
    dispatch(saveConfigs(keyCopy, configs.byKey[key].items, ''))
      .then(() => history.push(`/redigera/${keyCopy}`));
  }

  onActiveKeyChange(event) {
    const key = event.target.value;
    const { dispatch } = this.props;
    const config = this.props.configs.byKey[key];

    // If config is not in memory, we need to read it from server, in order to validate it before making it active,
    // since someone else might have edited it.
    if (!config) {
      dispatch(readConfigs(key))
        .then(() => {
          const config = this.props.configs.byKey[key];

          if (!checkConfigAndPromptIfError(config)) {
            return;
          }

          dispatch(activateConfig(key));
        });
      return;
    }

    if (!checkConfigAndPromptIfError(config)) {
      return;
    }

    dispatch(activateConfig(key));
  }

  promptForName() {
    const { configs } = this.props;
    const suggestedKey = getNextNameWithSequenceNumber(defaultKey(), configs.allKeys);
    const keyCopy = window.prompt('Välj namna på utskick', suggestedKey);

    if (!keyCopy) {
      return null;
    }

    if (configs.byKey[keyCopy]) {
      alert(`Utskicket "${keyCopy}" finns redan. Försök igen.`);
      return null;
    }

    const error = validateKey(keyCopy);

    if (error) {
      switch(error.code) {
        case KeyErrorCode.EMPTY_KEY:
          alert('Namnet på utskicket får inte vara tomt. Försök igen.');
          return null;
        case KeyErrorCode.INVALID_SPACE_CHARACTER:
          alert('Namnet på utskicket får inte innehålla mellanslag. Försök igen.');
          return null;
        case KeyErrorCode.INVALID_SPECIAL_CHARACTERS:
          alert('Namnet på utskicket får endast innehålla siffror, bokstäverna a-z, bindestreck eller paranteser. Försök igen.');
          return null;
        default:
          alert(error.message);
          return null;
      }
    }

    return keyCopy;
  }
}

Dashboard.propTypes = {
  configs: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  simulations: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  return {
    configs: state.configs,
    simulations: state.simulations,
  };
};

export default connect(mapStateToProps)(Dashboard);



// WEBPACK FOOTER //
// ./client/src/containers/dashboard/index.js